import { Link } from 'react-router';
import { CouncilClaimIntro } from '@/components/councils/council-claim-intro';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'West Northamptonshire has RED DfT ratings — does that guarantee my claim will succeed?',
    answer: 'No guarantee. The Department for Transport rates West Northamptonshire RED overall, RED on condition and RED on wider best practice — one of 13 red-rated authorities in England for 2025/26. That strengthens arguments about systemic maintenance pressure, but Section 58 still turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. Your photos, prior reports and the road\'s survey classification matter more than the headline rating alone.',
  },
  {
    question: 'Only 7.57% of B/C roads are in good condition — how does that help my claim?',
    answer: 'It shows network-level deterioration, not an isolated pothole. Good-condition B/C roads fell from 15.83% in 2020 to 7.57% in 2024 while amber B/C roads rose to 76.33% and red to 16.10% — meaning 92.43% of the 726km B/C network now needs maintenance soon or now. When claiming on a B or C road, you can ask what the council\'s annual camera survey recorded for your route and what action followed an amber or red classification.',
  },
  {
    question: 'What if my pothole was on an unclassified residential road?',
    answer: 'U-roads make up 1,475.91km — 59% of West Northamptonshire\'s 2,489km carriageway network. The council publishes only RED-condition percentages for U-roads (16.39% in 2024); amber and green are not reported. The council does conduct annual high-resolution camera surveys across the network, but without full RAG data for U-roads your claim should focus on inspection records, prior FixMyStreet reports and photos showing defect age on your specific street.',
  },
  {
    question: 'The council spent £24.1m capital in 2024/25 — more than double its DfT allocation. Can I still claim?',
    answer: 'Yes. The Spend scorecard is AMBER, not a bar to compensation. Section 58 turns on the specific defect, not aggregate budgets. In 2024/25 the council estimated just 4% of maintenance spend on preventative treatments (surface dressing, micro asphalt and retexturing) while 41% was reactive — including pothole filling. High capital volume alongside a RED condition scorecard suggests money is not translating into stable network condition at the point your pothole formed.',
  },
  {
    question: 'Why is best practice RED when the council lists Roadmender and thermal patching?',
    answer: 'The DfT wider best practice scorecard is separate from what a council lists in its transparency report. West Northamptonshire describes embedded innovations including Roadmender polymer-modified repairs and thermal patching, and states its surveys align with BSI PAS2161 ahead of the 2026/27 mandate — yet the DfT still rates wider best practice RED. That gap between claimed innovation and official assessment is relevant to Section 58: you can ask whether the council\'s actual maintenance system — not its brochure — was reasonable for your defect.',
  },
  {
    question: 'The council filled 14,849 potholes in 2024/25 — does that mean the roads are maintained?',
    answer: 'Not necessarily for your specific defect. Potholes are the most reported defect type through FixMyStreet, and the council filled an estimated 14,019 per year on average over five years — roughly 41 repairs per day in 2024/25. That volume of reactive work alongside falling B/C green condition (down 52% since 2020) indicates defects are forming continuously. Reactive patching does not prove your pothole was inspected, prioritised or permanently repaired within a reasonable time before your incident.',
  },
  {
    question: 'How do I report a pothole to West Northamptonshire Council?',
    answer: 'Report potholes and highway problems via the council\'s online form at westnorthants.gov.uk or through FixMyStreet at fix.westnorthants.gov.uk — reports on FixMyStreet.com are routed to the same system. The council states online reports may not be reviewed for up to five working days. For urgent safety issues call 0300 126 7000 (Mon–Fri 9am–5pm) or 01604 651074 out of hours. Prior reports of the same defect strengthen a claim by demonstrating notice before your incident.',
  },
];

export function WestNorthamptonshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="West Northamptonshire Pothole Claims: How to Claim | Fixtyer"
        description="How to request West Northamptonshire Council’s claim form, document pothole damage and submit your evidence. Free letter template and local road-maintenance data."
        canonical="/councils/west-northamptonshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'West Northamptonshire', url: '/councils/west-northamptonshire' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="West Northamptonshire Council"
        councilSlug="west-northamptonshire"
        rating="red"
      />
      <SpeakableSchema
        url="/councils/west-northamptonshire"
        headline="West Northamptonshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">West Northamptonshire</span>
          </nav>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            West Northamptonshire pothole claims: how to claim
          </h1>
          <CouncilClaimIntro area="West Northamptonshire" />
          <h2 className="font-display text-2xl font-bold mb-5">West Northamptonshire road maintenance: the evidence</h2>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="red" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            West Northamptonshire Council — a unitary authority formed in April 2021, distinct from North
            Northamptonshire and the former Northamptonshire County Council — maintains 2,489km of roads. The DfT
            rates it <strong className="text-rose-400">RED</strong> overall with{' '}
            <strong className="text-rose-400">RED</strong> condition and{' '}
            <strong className="text-rose-400">RED</strong> wider best practice scorecards. Just{' '}
            <strong className="text-rose-400">7.57% of B/C roads</strong> remain in good condition — down 52%
            since 2020 — while the council filled an estimated{' '}
            <strong className="text-rose-400">14,849 potholes in 2024/25</strong> and spent only{' '}
            <strong className="text-amber-400">4% on preventative maintenance</strong> that year.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">7.57%</div>
            <div className="text-white font-medium mt-2">Of B/C roads in good condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Down from 15.83% in 2020. Meanwhile 76.33% are amber and 16.10% red — 92.43% of the 726km B/C
              network now needs maintenance soon or now.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            2,489km of Roads — Mostly Unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from West Northamptonshire's April 2026 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">2,489km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">1,476km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">59% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">726km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">287km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
              <div className="text-xs text-surface-500 mt-1">Principal corridors</div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Scope caveat:</strong> The strategic motorway and trunk road network (M1, M45, A5, A14 and A43)
              is maintained by National Highways, not West Northamptonshire Council. Private and unadopted streets may
              also fall outside council control. West Northamptonshire became a unitary authority in April 2021; separate
              DfT funding allocations for North and West Northamptonshire began in April 2022 — so 2020/21 and 2021/22
              spending figures in the report are not directly comparable.
            </p>
          </div>

          <QuoteCard
            quote="Potholes remain as the defect that is most reported through our customer reporting tool Fix My Street. In addition to customer reporting, we also carry out regular inspections on the highway to identify any safety repairs that are needed."
            source="West Northamptonshire Council Local Highways Maintenance Transparency Report (April 2026)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What RED Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Annual high-resolution camera surveys from 2020 — with U-road amber/green data not published
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> Since 2020, condition assessments use high-resolution cameras and
              specialist software, aligned with BSI PAS2161 ahead of the mandatory 2026/27 standard. Five-category survey
              results are adjusted into three reporting categories (red, amber, green). From 2026/27 a new PAS2161
              methodology with five published categories will apply — figures before and after that change may not be
              directly comparable.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">B and C roads (726km) — 52% collapse in green condition</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">14.40%</td>
                    <td className="px-4 py-2 text-center">69.77%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">15.83%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">17.70%</td>
                    <td className="px-4 py-2 text-center">68.70%</td>
                    <td className="px-4 py-2 text-center">13.60%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">19.20%</td>
                    <td className="px-4 py-2 text-center">70.20%</td>
                    <td className="px-4 py-2 text-center">10.60%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">14.93%</td>
                    <td className="px-4 py-2 text-center">76.56%</td>
                    <td className="px-4 py-2 text-center">8.51%</td>
                  </tr>
                  <tr className="bg-rose-100">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">16.10%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">76.33%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7.57%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              Good-condition B/C roads fell from 15.83% to 7.57% — a 52% relative decline. Amber B/C roads rose from
              69.77% to 76.33%. This is the road class where most non-residential pothole claims sit.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (287km) — green share down a third</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">4.20%</td>
                    <td className="px-4 py-2 text-center">61.20%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">34.60%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center">4.00%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">72.82%</td>
                    <td className="px-4 py-2 text-center">23.18%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road green condition fell from 34.60% to 23.18% — a 33% relative decline — while amber A-roads rose
              from 61.20% to 72.82%. A-roads are 12% of the carriageway network.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (1,476km) — RED only published</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red (U-roads only)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">19.00%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">18.60%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">16.39%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              The council publishes only RED-condition percentages for U-roads — roughly 242km of residential and estate
              routes in the worst category at the 2024 survey. Amber and green U-road percentages are not reported,
              limiting network-level evidence for claims on local streets.
            </p>
          </div>

          <QuoteCard
            quote="Since 2020 road condition assessments on the West Northamptonshire local road network have been captured annually, using high-resolution cameras mounted on vehicles that capture thousands of detailed images of the road surface as the vehicle drives along."
            source="West Northamptonshire Council Local Highways Maintenance Transparency Report (April 2026)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            AMBER spend scorecard — but preventative maintenance hit 4% in 2024/25 while reactive work reached 41%
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">West Northamptonshire highway maintenance spending</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center">14,618</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">14,618</td>
                  <td className="px-4 py-3 text-center">6,484</td>
                  <td className="px-4 py-3 text-center text-emerald-400">16%</td>
                  <td className="px-4 py-3 text-center">38%</td>
                </tr>
                <tr className="bg-rose-900/30">
                  <td className="px-4 py-3 font-bold">2024/25</td>
                  <td className="px-4 py-3 text-center">12,026</td>
                  <td className="px-4 py-3 text-center font-bold">24,101</td>
                  <td className="px-4 py-3 text-center">4,002</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-400">4%</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-400">41%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">13,923</td>
                  <td className="px-4 py-3 text-center">19,088</td>
                  <td className="px-4 py-3 text-center">2,958</td>
                  <td className="px-4 py-3 text-center">14%</td>
                  <td className="px-4 py-3 text-center">38%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">10,669</td>
                  <td className="px-4 py-3 text-center">9,801</td>
                  <td className="px-4 py-3 text-center">4,823</td>
                  <td className="px-4 py-3 text-center">7%</td>
                  <td className="px-4 py-3 text-center">37%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <QuoteCard
              quote="Preventative maintenance is based on the amount spent on surface dressing, micro asphalt and retexturing treatments. These are treatments that are used to extend the life of the asset."
              source="West Northamptonshire Council Local Highways Maintenance Transparency Report (April 2026)"
            />
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is AMBER, not GREEN</h3>
              <p className="text-brand-200 text-sm">
                Capital spend exceeded the DfT allocation in 2024/25 (£24.1m vs £12.0m) and matches it exactly in the
                2025/26 projection (£14.6m). But the DfT Spend scorecard also reflects how effectively that money
                prevents deterioration. With just 4% preventative share in 2024/25 — while B/C green condition kept
                falling — the council earns AMBER despite high capital volumes.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30 text-center">
              <div className="text-3xl font-bold text-rose-400">4%</div>
              <div className="text-brand-200 text-sm mt-1">Preventative share 2024/25</div>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30 text-center">
              <div className="text-3xl font-bold text-amber-400">41%</div>
              <div className="text-brand-200 text-sm mt-1">Reactive share 2024/25</div>
            </div>
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30 text-center">
              <div className="text-3xl font-bold text-emerald-400">16%</div>
              <div className="text-brand-200 text-sm mt-1">Preventative projected 2025/26</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            70,097 Pothole Repairs in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — the council's most-reported defect type via FixMyStreet
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">West Northamptonshire estimated potholes filled 2020-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">14,769</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">13,605</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">12,300</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">14,574</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">14,849</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">70,097</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-amber-600">14,019</div>
              <div className="text-amber-800 font-medium">Average per year</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-rose-600">41</div>
              <div className="text-rose-800 font-medium">Potholes per day (2024/25)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-6 text-center">
              <div className="text-4xl font-bold text-brand-900">5 years</div>
              <div className="text-surface-700 font-medium">No sustained decline</div>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Reactive treadmill, not network recovery</h3>
            <p className="text-brand-200 mb-4">
              Pothole counts stayed between 12,300 and 14,849 annually while B/C green condition fell from 15.83%
              to 7.57%. The council filled roughly 41 potholes per day in 2024/25 yet preventative spend that year was
              just 4%. That pattern — high reactive volume alongside deteriorating condition data — is evidence of
              ongoing failure demand, not proof your specific defect was unavoidable.
            </p>
          </div>
        </div>
      </section>

      {/* Innovation vs RED best practice */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Innovation Claims vs RED Best Practice
          </h2>
          <p className="text-surface-600 mb-8">
            The council lists Roadmender and thermal patching — the DfT rates wider best practice RED
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What the council reports</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Roadmender: polymer-modified bitumen permanent repairs with minimal waste</li>
                <li>• Thermal patching: reworks existing asphalt using heat without full excavation</li>
                <li>• Annual high-resolution camera surveys aligned with BSI PAS2161</li>
                <li>• 2025/26 plan: 6.79km micro asphalt, 13.2km surface dressing, 1.4km resurfacing</li>
              </ul>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">What the DfT measures</h3>
              <p className="text-rose-800 text-sm mb-3">
                The wider best practice scorecard assesses whether councils adopt recognised efficiency and innovation
                practices — including technologies to fix and prevent potholes more efficiently. West Northamptonshire
                is RED on that measure despite its transparency report listing embedded innovations.
              </p>
              <p className="text-rose-800 text-sm">
                For 2025/26 the council plans to treat roughly 21.4km of its 2,489km network with preventative
                carriageway works — under 1% of total length in a single year.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="West Northamptonshire Council became a Unitary Authority in April 2021 and individual funding allocations for North Northamptonshire and West Northamptonshire were first made in April 2022."
            source="West Northamptonshire Council Local Highways Maintenance Transparency Report (April 2026)"
          />
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How West Northamptonshire says it knows network condition — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey and inspection regime</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Annual camera surveys on A, B/C and U roads since 2020</li>
                <li>• Regular safety inspections alongside FixMyStreet customer reporting</li>
                <li>• Pothole response times set by risk — not every defect gets immediate permanent repair</li>
                <li>• Online reports may not be reviewed for up to five working days</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Funding limitation admission</h3>
              <p className="text-amber-800 text-sm">
                On highway structures, the council states it is "recognised that limitations on funding may restrict
                maintenance spending in the short and medium term and in such situations the network will need to be
                proactively managed to maintain safety and availability." That published constraint is relevant when
                asking what was done on your road after it was classified amber or red.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, West Northamptonshire
              must maintain public highways. To defend a claim under <strong className="text-white">Section 58</strong>,
              it must show a reasonable system for inspecting and repairing the specific defect — not just publish
              camera survey technology or a RED DfT scorecard.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• What did the annual camera survey record for your road section — and when?</li>
              <li>• Were there prior FixMyStreet or council reports giving actual notice of the same defect?</li>
              <li>• Does photographic evidence show defect age beyond the council's inspection or response interval?</li>
              <li>• If your road was amber or red at survey, what preventative or reactive work followed?</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a RED-Rated Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: West Northamptonshire's RED scorecards give you leverage — but the council will still argue its inspection system
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AMBER spend — capital exceeded DfT allocation in 2024/25 (£24.1m vs £12.0m)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual camera surveys on all road classes since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented innovations (Roadmender, thermal patching) and PAS2161-aligned surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Regular safety inspections plus FixMyStreet reporting channel
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Preventative share projected to rise to 16% in 2025/26
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a Section 58 defence citing annual surveys and risk-based pothole response times.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What works in yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED overall, condition and wider best practice DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only 7.57% of B/C roads green; 92.43% amber or red
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 1,476km of U-roads with only RED percentages published (16.39% in 2024)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 70,097 pothole repairs in five years — ~41 per day in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Just 4% preventative spend in 2024/25 while condition kept deteriorating
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits funding limitations may restrict maintenance spending
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with annual surveys and 14,000+ annual pothole fills, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior FixMyStreet reports of the same pothole — proof of actual notice on the most-reported defect type</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Your road's survey classification — amber or red B/C data, or U-road RED percentage context</li>
              <li>• The gap between claimed innovation and the DfT's RED best practice rating</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites
              West Northamptonshire's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to West Northamptonshire Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. The council states online reports may not be
            reviewed for up to five working days — do this before claiming, and tell us when you reported it so we can
            reference it in your pack.
          </p>
          <a
            href="https://www.westnorthants.gov.uk/highway-maintenance/report-potholes-or-highway-problem"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — westnorthants.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in West Northamptonshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            RED scorecards and 14,849 pothole fills demand a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 7.57% B/C condition context</li>
                <li>• No prior FixMyStreet search</li>
                <li>• No RED best practice rebuttal</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 7.57% B/C green condition documented</li>
                <li>✅ RED DfT scorecards cited</li>
                <li>✅ 14,849 pothole fills in 2024/25 referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to West Northamptonshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=west-northamptonshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My West Northamptonshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.westnorthants.gov.uk/highway-maintenance/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              West Northamptonshire Council Local Highways Maintenance Transparency Report (April 2026)
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
